/**
 * Task Dashboard
 *
 * Main content when backend is healthy: view selector, filters, task list,
 * task detail panel, and chat input. Handles completion undo window.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import clsx from "clsx";
import {
  PanelLeftClose,
  PanelLeftOpen,
  Settings as SettingsIcon,
  BarChart3,
  Clock,
  CheckCircle2,
  Share2,
  Plus,
  Mic,
} from "lucide-react";
import { ShareListModal } from "../../components/ShareListModal";
import { CalendarWidget } from "../../components/CalendarWidget";
import { Layout } from "../../components/Layout";
import { TaskList } from "../../components/TaskList";
import { useStableOrder } from "../../lib/stableOrder";
import { ChatPanel } from "../../components/ChatPanel";
import { ChatToggleButton } from "../../components/ChatToggleButton";
import { useChatPanelOpen } from "../../hooks/useChatPanelOpen";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { MobileChatView } from "../../components/MobileChatView";
import { MobileTopBar } from "../../components/MobileTopBar";
import { MobileBottomFade } from "../../components/MobileBottomFade";
import type { MobileTab } from "../../components/MobileTabBar";
import { MobileDrawer } from "../../components/MobileDrawer";
import { PwaInstallBanner } from "../../components/PwaInstallBanner";
import {
  QuickFilters,
  type SortOption,
  type SortDirection,
} from "../../components/QuickFilters";
import { SettingsLayout } from "../../components/settings/SettingsLayout";
import { ProductivityInsights } from "../../components/ProductivityInsights";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useTasks } from "../../hooks/useTasks";
import { useSemanticSearch } from "../../hooks/useSemanticSearch";
import { useViews } from "../../hooks/useViews";
import { useSharedLists } from "../../hooks/useSharedLists";
import { fetchApi } from "../../api/fetch";
import { useSettings } from "../../hooks/useSettings";
import { useProjects } from "../../hooks/useProjects";
import { useDecomposeSuggestions } from "../../hooks/useDecomposeSuggestions";
import { getUserSettings, updateSortSettings } from "../../api/userSettings";
import {
  completeTask,
  reopenTask,
  getTask,
  type ChatPageContext,
} from "../../api/client";
import { dismissMergeNotice, getMe } from "../../api/auth";
import { getViewIconComponent } from "../../utils/viewIcons";
import { DetailPanelLayout } from "../../components/DetailPanelLayout";
import { QuickAddFab } from "../../components/QuickAddFab";
import { BrainDumpOverlay } from "../../components/BrainDumpOverlay";
import { TaskCreateForm } from "../../components/TaskCreateForm";
import { GettingStartedChecklist } from "../../components/onboarding/GettingStartedChecklist";
import { UpgradeModal } from "../../components/UpgradeModal";
import type { Task } from "../../types";
import { sortTasks } from "./sortTasks";
import { useOnboardingProgress } from "../../hooks/useOnboardingProgress";
import { syncPerUserLocalState } from "../../utils/authStateTransition";

/** Duration in ms before completed tasks are removed from view (2 minutes) */
const COMPLETION_UNDO_WINDOW_MS = 2 * 60 * 1000;

export function Dashboard() {
  return <DashboardInner />;
}

function DashboardInner() {
  useSettings(); // Load settings into context
  const { visibleViews } = useViews();
  const { lists: sharedLists } = useSharedLists();
  const [mobileTab, setMobileTab] = useState<MobileTab>("tasks");
  const { projectsMap } = useProjects();
  const [currentView, setCurrentView] = useState<string>("today");
  const [, setSearchParams] = useSearchParams();
  // /tasks/:taskId deep-link param (from email links). Undefined at /app.
  const { taskId: urlTaskId } = useParams();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [calendarRefreshNonce, setCalendarRefreshNonce] = useState(0);
  const [showSettings, setShowSettings] = useState(() => {
    // Auto-open settings if URL has ?tab=... (deep link from emails, etc.)
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).has("tab");
  });
  const [showInsights, setShowInsights] = useState(false);

  // Bump CalendarWidget refresh when Settings closes — user may have just connected/disconnected Google
  const wasShowingSettings = useRef(showSettings);
  useEffect(() => {
    if (wasShowingSettings.current && !showSettings) {
      setCalendarRefreshNonce((n) => n + 1);
    }
    wasShowingSettings.current = showSettings;
  }, [showSettings]);
  const [isBrainDumpOpen, setIsBrainDumpOpen] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("danny_sidebar_collapsed") === "true";
  });

  const [sortBy, setSortBy] = useState<SortOption>("due");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalStripeConfigured, setUpgradeModalStripeConfigured] =
    useState(true);
  const [showMergeNotice, setShowMergeNotice] = useState(false);
  const { data: onboardingProgress } = useOnboardingProgress();
  const isOnboarding = onboardingProgress && !onboardingProgress.done;
  const {
    isOpen: isChatPanelOpen,
    setOpen: setChatPanelOpen,
    toggle: toggleChatPanel,
  } = useChatPanelOpen();

  // Below md (1024px) the app falls back to the mobile layout (handled by
  // Tailwind md:hidden / hidden md:flex classes) — no chat auto-close needed.
  const isBelowSidebarBreakpoint = useMediaQuery("(max-width: 1279px)");

  useEffect(() => {
    localStorage.setItem("danny_sidebar_collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Auto-collapse sidebar at mid-widths when both chat and task detail are open
  useEffect(() => {
    if (
      isBelowSidebarBreakpoint &&
      isChatPanelOpen &&
      selectedTask &&
      !sidebarCollapsed
    ) {
      setSidebarCollapsed(true);
    }
  }, [
    isBelowSidebarBreakpoint,
    isChatPanelOpen,
    selectedTask,
    sidebarCollapsed,
  ]);

  useEffect(() => {
    getMe()
      .then((me) => {
        if (me?.pendingMergeNotice) setShowMergeNotice(true);
        if (syncPerUserLocalState(me?.id ?? null)) {
          window.location.reload();
        }
      })
      .catch(() => {});
  }, []);

  // Cmd/Ctrl+J toggles the desktop chat panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        toggleChatPanel();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleChatPanel]);

  // Default to 'all' view during onboarding so decomposed subtasks are visible
  useEffect(() => {
    if (isOnboarding && currentView === "today") {
      setCurrentView("all");
    }
  }, [isOnboarding]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle ?view=shared:listId from public page redirect for authenticated users
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get("view");
    if (view?.startsWith("shared:")) {
      setCurrentView(view);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleDismissMergeNotice = async () => {
    setShowMergeNotice(false);
    await dismissMergeNotice().catch(() => {});
  };

  const pendingCompletionTimeoutsRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());
  const [pendingCompletions, setPendingCompletions] = useState<
    Map<string, { task: Task; originalIndex: number }>
  >(new Map());

  // Tasks created via chat that may not match the current view filter —
  // pinned at the top until the user changes views or sends another message.
  const [ephemeralTasks, setEphemeralTasks] = useState<Task[]>([]);

  const isSharedListView = currentView.startsWith("shared:");
  const sharedListId = isSharedListView
    ? currentView.replace("shared:", "")
    : null;

  const {
    tasks: viewTasks,
    isLoading: viewLoading,
    refetch,
  } = useTasks(isSharedListView ? "today" : currentView, showCompleted);

  const { data: sharedListTaskData } = useQuery({
    queryKey: ["tasks", "shared-list", sharedListId],
    queryFn: async () => {
      if (!sharedListId) return { tasks: [] };
      return fetchApi<{ tasks: Task[] }>(`/shared-lists/${sharedListId}/tasks`);
    },
    enabled: !!sharedListId,
  });

  const sharedOrViewTasks = isSharedListView
    ? (sharedListTaskData?.tasks ?? [])
    : viewTasks;

  const {
    results: semanticResults,
    mode: searchMode,
    isLoading: searchLoading,
  } = useSemanticSearch(searchTerm);

  // When semantic search is active, use its results; otherwise use the view tasks.
  const tasks = semanticResults ?? sharedOrViewTasks;
  const isLoading = semanticResults !== null ? searchLoading : viewLoading;

  // Sync selectedTask from the latest task list — ensures the detail panel
  // reflects AI mutations (snooze, update, etc.) that trigger a query refetch.
  useEffect(() => {
    setSelectedTask((prev) => {
      if (!prev) return prev;
      const updated = tasks.find((t) => t.id === prev.id);
      return updated ?? prev;
    });
  }, [tasks]);

  // Auto-decompose toast notifications
  const { suggestions: decomposeSuggestions } = useDecomposeSuggestions();
  const [autoDecomposeMode, setAutoDecomposeMode] = useState<
    "off" | "suggest" | "auto"
  >("off");

  // Load the auto-decompose preference and default sort once on mount
  useEffect(() => {
    getUserSettings()
      .then((s) => {
        setAutoDecomposeMode(s.autoDecompose ?? "off");
        if (s.defaultSort) setSortBy(s.defaultSort);
        if (s.defaultSortDirection) setSortDirection(s.defaultSortDirection);
      })
      .catch(() => {});
  }, []);

  // Show toasts for new suggestions when auto mode is enabled
  useEffect(() => {
    if (autoDecomposeMode !== "auto" || decomposeSuggestions.length === 0)
      return;

    const STORAGE_KEY = "danny_decompose_toasted";
    let toasted: string[] = [];
    try {
      toasted = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      /* ignore */
    }

    const newSuggestions = decomposeSuggestions.filter(
      (s) => !toasted.includes(s.id),
    );
    if (newSuggestions.length === 0) return;

    for (const suggestion of newSuggestions) {
      const task = tasks.find((t) => t.id === suggestion.taskId);
      const taskLabel = task?.content ?? "a task";
      toast(
        `Danny broke down "${taskLabel}" into ${suggestion.suggestions.length} subtasks`,
        {
          description: "Tap to review",
          action: {
            label: "Review",
            onClick: () => {
              const matchedTask = tasks.find((t) => t.id === suggestion.taskId);
              if (matchedTask) {
                handleTaskSelect(matchedTask);
              }
            },
          },
        },
      );
      toasted.push(suggestion.id);
    }

    try {
      // Keep only the last 200 IDs to avoid unbounded growth
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toasted.slice(-200)));
    } catch {
      /* ignore */
    }
  }, [decomposeSuggestions, autoDecomposeMode, tasks]);

  // Deep-link: ?task=TASK_ID opens task detail on load
  // Deep-link: ?complete=TASK_ID marks task done and opens it
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const taskId = params.get("task") || params.get("complete");
    const shouldComplete = params.has("complete");
    if (!taskId) return;

    // Clean URL immediately
    const clean = new URL(window.location.href);
    clean.searchParams.delete("task");
    clean.searchParams.delete("complete");
    window.history.replaceState({}, "", clean.toString());

    // Fetch, optionally complete, then select
    getTask(taskId)
      .then(async (task) => {
        if (!task) return;
        if (shouldComplete && !task.isCompleted) {
          await completeTask(task.id).catch(() => {});
          const updated = await getTask(task.id).catch(() => task);
          setSelectedTask(updated ?? task);
        } else {
          setSelectedTask(task);
        }
      })
      .catch(() => {});
  }, []);

  const chatContext = useMemo((): ChatPageContext => {
    const currentViewObj = visibleViews.find((v) => v.slug === currentView);
    const context: ChatPageContext = {
      currentView,
      currentViewName: currentViewObj?.name,
    };
    if (selectedTask) {
      context.selectedTaskId = selectedTask.id;
      context.selectedTaskContent = selectedTask.content;
      context.selectedTaskDescription = selectedTask.description;
      context.selectedTaskPriority = selectedTask.priority;
      context.selectedTaskDueDate = selectedTask.due?.date;
      context.selectedTaskCategory = selectedTask.metadata?.category;
      context.selectedTaskLabels = selectedTask.labels;
    }
    return context;
  }, [currentView, visibleViews, selectedTask]);

  // Completed view: preserve API sort order (completedAt desc) — don't re-sort client-side
  const sortedTasks =
    currentView === "completed"
      ? [...tasks]
      : sortTasks(tasks, sortBy, sortDirection);

  // Completed view: all tasks are already completed — don't filter them out
  const completedFilteredTasks =
    showCompleted || currentView === "completed"
      ? sortedTasks
      : sortedTasks.filter(
          (t) => !t.isCompleted || pendingCompletions.has(t.id),
        );

  // When semantic search is active, the backend already filters by relevance —
  // skip the local keyword filter but keep the completion filter and sort.
  const isSemanticActive = semanticResults !== null;
  const filteredTasks =
    !isSemanticActive && searchTerm
      ? completedFilteredTasks.filter((t) => {
          const term = searchTerm.toLowerCase();
          return (
            t.content.toLowerCase().includes(term) ||
            (t.description && t.description.toLowerCase().includes(term)) ||
            (t.labels &&
              t.labels.some((l: string) => l.toLowerCase().includes(term))) ||
            (t.metadata?.category &&
              t.metadata.category.toLowerCase().includes(term))
          );
        })
      : completedFilteredTasks;

  // Freeze row order during review: editing a task in place (e.g. bumping its
  // priority) must not re-sort the row out from under the user and yank their
  // scroll position. Only rebaseline the order when the sort/view/search
  // signature changes; otherwise keep existing rows where they are.
  const orderSig = `${currentView}|${sortBy}|${sortDirection}|${isSemanticActive}|${searchTerm}|${showCompleted}`;
  const stableFilteredTasks = useStableOrder(filteredTasks, orderSig);

  // Prepend ephemeral tasks (chat-created, may not match view filter) at the top.
  // Deduplicate: if a refetch brings the task into the normal list, drop the ephemeral copy.
  const filteredTaskIds = new Set(stableFilteredTasks.map((t) => t.id));
  const displayTasks = [
    ...ephemeralTasks.filter((t) => !filteredTaskIds.has(t.id)),
    ...stableFilteredTasks,
  ];

  const pendingCompletionIds = new Set(pendingCompletions.keys());

  const handleViewChange = useCallback((viewSlug: string) => {
    setCurrentView(viewSlug);
    setSelectedTask(null);
    setEphemeralTasks([]);
  }, []);

  const handleTaskSelect = useCallback((task: Task) => {
    setSelectedTask(task);
    setIsCreatingTask(false);
  }, []);

  // Deep-link: /tasks/:taskId (from email links) opens that task's detail once
  // tasks have loaded. A ref guards against a later refetch reopening a detail
  // the user already closed. If the task is missing (e.g. deleted), we just
  // leave the list visible rather than erroring.
  const deepLinkOpenedRef = useRef(false);
  useEffect(() => {
    if (deepLinkOpenedRef.current || !urlTaskId || isLoading) return;
    const target = tasks.find((t) => t.id === urlTaskId);
    if (target) {
      deepLinkOpenedRef.current = true;
      handleTaskSelect(target);
    }
  }, [urlTaskId, isLoading, tasks, handleTaskSelect]);

  const handleTaskSelectById = useCallback(
    async (taskId: string) => {
      const localTask = tasks.find((t) => t.id === taskId);
      if (localTask) {
        setSelectedTask(localTask);
        return;
      }
      try {
        const task = await getTask(taskId);
        setSelectedTask(task);
      } catch (err) {
        console.error("Failed to load task:", err);
      }
    },
    [tasks],
  );

  const handleTaskClose = useCallback(() => {
    setSelectedTask(null);
  }, []);

  // Called from TaskList checkbox — needs to make the API call itself
  const handleTaskComplete = useCallback(
    (taskId: string) => {
      const taskIndex = tasks.findIndex((t) => t.id === taskId);
      const task = tasks[taskIndex];
      if (!task) return;

      setPendingCompletions((prev) => {
        const next = new Map(prev);
        next.set(taskId, { task, originalIndex: taskIndex });
        return next;
      });

      if (selectedTask?.id === taskId) {
        setSelectedTask(null);
      }

      const timeoutId = setTimeout(() => {
        setPendingCompletions((prev) => {
          const next = new Map(prev);
          next.delete(taskId);
          return next;
        });
        pendingCompletionTimeoutsRef.current.delete(taskId);
        refetch();
      }, COMPLETION_UNDO_WINDOW_MS);

      pendingCompletionTimeoutsRef.current.set(taskId, timeoutId);

      completeTask(taskId).catch((error) => {
        console.error("Failed to complete task:", error);
        setPendingCompletions((prev) => {
          const next = new Map(prev);
          next.delete(taskId);
          return next;
        });
        clearTimeout(timeoutId);
        pendingCompletionTimeoutsRef.current.delete(taskId);
      });
    },
    [tasks, selectedTask, refetch],
  );

  // Called from TaskDetail after it has already made the API call.
  // Only handles UI: close panel, schedule refetch, undo window.
  // Works even if the task isn't in the current view (e.g. opened via deep-link).
  const handleTaskCompleteFromDetail = useCallback(
    (taskId: string) => {
      const taskIndex = tasks.findIndex((t) => t.id === taskId);
      const task =
        tasks[taskIndex] ?? (selectedTask?.id === taskId ? selectedTask : null);
      if (!task) {
        // Task not in current view — just close the panel and refetch
        setSelectedTask(null);
        refetch();
        return;
      }

      setPendingCompletions((prev) => {
        const next = new Map(prev);
        next.set(taskId, {
          task,
          originalIndex: taskIndex === -1 ? 0 : taskIndex,
        });
        return next;
      });

      setSelectedTask(null);

      const timeoutId = setTimeout(() => {
        setPendingCompletions((prev) => {
          const next = new Map(prev);
          next.delete(taskId);
          return next;
        });
        pendingCompletionTimeoutsRef.current.delete(taskId);
        refetch();
      }, COMPLETION_UNDO_WINDOW_MS);

      pendingCompletionTimeoutsRef.current.set(taskId, timeoutId);
    },
    [tasks, selectedTask, refetch],
  );

  const handleTaskUndo = useCallback((taskId: string) => {
    const timeoutId = pendingCompletionTimeoutsRef.current.get(taskId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      pendingCompletionTimeoutsRef.current.delete(taskId);
    }
    setPendingCompletions((prev) => {
      const next = new Map(prev);
      next.delete(taskId);
      return next;
    });
    reopenTask(taskId).catch((error) => {
      console.error("Failed to undo task completion:", error);
    });
  }, []);

  const handleTaskReopen = useCallback(
    (taskId: string) => {
      // Optimistically update selectedTask so the UI reflects the change immediately
      setSelectedTask((prev) =>
        prev?.id === taskId ? { ...prev, isCompleted: false } : prev,
      );
      reopenTask(taskId)
        .then(() => refetch())
        .catch((error) => {
          console.error("Failed to reopen task:", error);
          // Revert on failure
          setSelectedTask((prev) =>
            prev?.id === taskId ? { ...prev, isCompleted: true } : prev,
          );
        });
    },
    [refetch],
  );

  const handleSortChange = useCallback(
    (newSortBy: SortOption, newDirection: SortDirection) => {
      setSortBy(newSortBy);
      setSortDirection(newDirection);
      // Persist selection as the user's default (fire-and-forget)
      updateSortSettings(newSortBy, newDirection).catch(() => {});
    },
    [],
  );

  const handleShowCompletedChange = useCallback((show: boolean) => {
    setShowCompleted(show);
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleTaskSuggestDecompose = useCallback((task: Task) => {
    setSelectedTask(task);
    // The SubtasksList will detect the pending suggestion and auto-show it
  }, []);

  const handleChatResponse = useCallback(() => {
    refetch();
    window.dispatchEvent(new CustomEvent("danny-views-updated"));
  }, [refetch]);

  // Listen for AI-driven navigation events
  useEffect(() => {
    const handler = (e: Event) => {
      const viewSlug = (e as CustomEvent).detail?.viewSlug;
      if (viewSlug) handleViewChange(viewSlug);
    };
    window.addEventListener("danny-navigate-view", handler);
    return () => window.removeEventListener("danny-navigate-view", handler);
  }, [handleViewChange]);

  useEffect(() => {
    const handler = (e: Event) => {
      const taskId = (e as CustomEvent).detail?.taskId;
      if (taskId) handleTaskSelectById(taskId);
    };
    window.addEventListener("danny-select-task", handler);
    return () => window.removeEventListener("danny-select-task", handler);
  }, [handleTaskSelectById]);

  // Fetch and pin chat-created tasks at the top, even if they don't match the
  // current view filter. Cleared when the user navigates or sends the next message.
  useEffect(() => {
    const onCreated = (e: Event) => {
      const taskId = (e as CustomEvent).detail?.taskId;
      if (!taskId) return;
      getTask(taskId)
        .then((task) => {
          if (!task) return;
          setEphemeralTasks((prev) => {
            if (prev.some((t) => t.id === task.id)) return prev;
            return [task, ...prev];
          });
        })
        .catch(() => {});
    };
    const onUserMessage = () => setEphemeralTasks([]);
    window.addEventListener("danny-task-created", onCreated);
    window.addEventListener("danny-user-message", onUserMessage);
    return () => {
      window.removeEventListener("danny-task-created", onCreated);
      window.removeEventListener("danny-user-message", onUserMessage);
    };
  }, []);

  useEffect(() => {
    const handleLimitReached = (e: Event) => {
      const detail = (e as CustomEvent<{ stripeConfigured?: boolean }>).detail;
      setUpgradeModalStripeConfigured(detail?.stripeConfigured !== false);
      setShowUpgradeModal(true);
    };
    window.addEventListener("danny:limit-reached", handleLimitReached);
    return () =>
      window.removeEventListener("danny:limit-reached", handleLimitReached);
  }, []);

  // The Getting-started checklist (and any other surface) requests the chat by
  // dispatching `danny:chat:open`. On mobile the chat surface only mounts when
  // mobileTab === "chat", so switch to it here. On desktop the drawer is opened
  // separately via useChatPanelOpen; this listener is a no-op visual change.
  useEffect(() => {
    const handleChatOpen = () => setMobileTab("chat");
    window.addEventListener("danny:chat:open", handleChatOpen);
    return () =>
      window.removeEventListener("danny:chat:open", handleChatOpen);
  }, []);

  const handleTaskUpdate = useCallback(
    (updatedTask: Task) => {
      if (selectedTask?.id === updatedTask.id) {
        setSelectedTask(updatedTask);
      }
      refetch();
    },
    [selectedTask, refetch],
  );

  const activeSharedList = sharedLists.find(
    (l) => `shared:${l.id}` === currentView,
  );
  const breadcrumb = showInsights
    ? "Insights"
    : showSettings
      ? "Settings"
      : (activeSharedList?.name ??
        visibleViews.find((v) => v.slug === currentView)?.name ??
        currentView);

  return (
    <Layout
      breadcrumb={breadcrumb}
      onSettingsClick={() => {
        setShowInsights(false);
        setShowSettings(true);
        setChatPanelOpen(false);
      }}
      onInsightsClick={() => {
        setShowSettings(false);
        setShowInsights(true);
        setChatPanelOpen(false);
      }}
    >
      {showMergeNotice && (
        <div className="flex items-center justify-between px-4 py-2 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800 text-sm text-primary-700 dark:text-primary-300">
          <span>
            We connected your Google account to your existing Danny account. You
            can now sign in with either.
          </span>
          <button
            onClick={handleDismissMergeNotice}
            className="ml-4 shrink-0 font-medium hover:text-primary-900 dark:hover:text-primary-100 transition-colors"
          >
            Got it
          </button>
        </div>
      )}

      {/* App shell: sidebar + main content */}
      <div className="flex h-full min-h-0 overflow-hidden">
        {/* ── Sidebar ── */}
        <aside
          className={clsx(
            "hidden md:flex flex-col shrink-0 transition-all duration-200",
            "bg-surface-raised-light dark:bg-surface-raised-dark border-r border-border-subtle-light dark:border-border-subtle-dark",
            showSettings || showInsights
              ? "md:hidden"
              : sidebarCollapsed
                ? "w-12"
                : "w-56",
          )}
        >
          {/* Toggle button */}
          <div
            className={clsx(
              "flex shrink-0 pt-2 pb-1",
              sidebarCollapsed ? "justify-center" : "justify-end px-2",
            )}
          >
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-1.5 rounded text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? (
                <PanelLeftOpen className="w-4 h-4" />
              ) : (
                <PanelLeftClose className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Nav items */}
          <nav className="flex-1 px-1.5 py-1 space-y-0.5 overflow-y-auto">
            {/* New Task + Brain Dump combo — primary CTA */}
            <div
              className={clsx(
                "flex w-full mb-2 shadow-sm hover:shadow-md rounded-md overflow-hidden",
                sidebarCollapsed && "flex-col",
              )}
            >
              <button
                onClick={() => {
                  setIsCreatingTask(true);
                  setSelectedTask(null);
                  setShowSettings(false);
                  setShowInsights(false);
                }}
                title={sidebarCollapsed ? "New Task" : undefined}
                className={clsx(
                  "flex items-center justify-center transition-colors duration-150 cursor-pointer",
                  sidebarCollapsed ? "w-full py-2.5" : "flex-1 gap-2 px-3 py-2",
                  "font-mono text-[13px] font-semibold tracking-wide",
                  isCreatingTask
                    ? "bg-primary-800 text-white"
                    : "bg-primary-700 hover:bg-primary-800 text-white",
                )}
              >
                <Plus className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                {!sidebarCollapsed && <span>New Task</span>}
              </button>
              {/* Divider */}
              <div
                className={clsx(
                  "bg-primary-900/30",
                  sidebarCollapsed ? "h-px w-full" : "w-px h-auto",
                )}
              />
              {/* Brain dump mic button */}
              <button
                onClick={() => setIsBrainDumpOpen(true)}
                title="Brain dump — speak multiple tasks"
                className={clsx(
                  "flex items-center justify-center transition-colors duration-150 cursor-pointer",
                  sidebarCollapsed ? "w-full py-2.5" : "px-3 py-2",
                  "bg-primary-700 hover:bg-primary-800 text-white",
                )}
              >
                <Mic className="w-4 h-4 shrink-0" strokeWidth={2.5} />
              </button>
            </div>

            {visibleViews.map((view) => {
              const ViewIcon = getViewIconComponent(view);
              const isActive = view.slug === currentView;
              return (
                <button
                  key={view.slug}
                  onClick={() => {
                    handleViewChange(view.slug);
                    setShowSettings(false);
                    setShowInsights(false);
                  }}
                  title={sidebarCollapsed ? view.name : undefined}
                  className={clsx(
                    "w-full flex items-center transition-all duration-150 rounded",
                    sidebarCollapsed
                      ? "justify-center px-0 py-2"
                      : "gap-2.5 px-3 py-2",
                    "font-mono text-[13px] font-medium",
                    isActive
                      ? "bg-primary-900/20 dark:bg-primary-900/20 text-primary-800 dark:text-primary-400"
                      : "text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark",
                  )}
                >
                  <ViewIcon
                    className={clsx(
                      "w-4 h-4 shrink-0",
                      isActive
                        ? "text-primary-700 dark:text-primary-500"
                        : "text-text-tertiary-light dark:text-text-tertiary-dark",
                    )}
                  />
                  {!sidebarCollapsed && (
                    <>
                      <span
                        className={clsx(
                          "opacity-40 font-normal",
                          isActive && "text-primary-700 dark:text-primary-400",
                        )}
                      >
                        /
                      </span>
                      {view.name}
                    </>
                  )}
                </button>
              );
            })}

            {/* Snoozed pseudo-view */}
            {(() => {
              const isActive = currentView === "snoozed";
              return (
                <button
                  key="snoozed"
                  onClick={() => {
                    handleViewChange("snoozed");
                    setShowSettings(false);
                    setShowInsights(false);
                  }}
                  title={sidebarCollapsed ? "Snoozed" : undefined}
                  className={clsx(
                    "w-full flex items-center transition-all duration-150 rounded",
                    sidebarCollapsed
                      ? "justify-center px-0 py-2"
                      : "gap-2.5 px-3 py-2",
                    "font-mono text-[13px] font-medium",
                    isActive
                      ? "bg-primary-900/20 dark:bg-primary-900/20 text-primary-800 dark:text-primary-400"
                      : "text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark",
                  )}
                >
                  <Clock
                    className={clsx(
                      "w-4 h-4 shrink-0",
                      isActive
                        ? "text-primary-700 dark:text-primary-500"
                        : "text-text-tertiary-light dark:text-text-tertiary-dark",
                    )}
                  />
                  {!sidebarCollapsed && (
                    <>
                      <span
                        className={clsx(
                          "opacity-40 font-normal",
                          isActive && "text-primary-700 dark:text-primary-400",
                        )}
                      >
                        /
                      </span>
                      Snoozed
                    </>
                  )}
                </button>
              );
            })()}

            {/* Completed pseudo-view */}
            {(() => {
              const isActive = currentView === "completed";
              return (
                <button
                  key="completed"
                  onClick={() => {
                    handleViewChange("completed");
                    setShowSettings(false);
                    setShowInsights(false);
                  }}
                  title={sidebarCollapsed ? "Completed" : undefined}
                  className={clsx(
                    "w-full flex items-center transition-all duration-150 rounded",
                    sidebarCollapsed
                      ? "justify-center px-0 py-2"
                      : "gap-2.5 px-3 py-2",
                    "font-mono text-[13px] font-medium",
                    isActive
                      ? "bg-primary-900/20 dark:bg-primary-900/20 text-primary-800 dark:text-primary-400"
                      : "text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark",
                  )}
                >
                  <CheckCircle2
                    className={clsx(
                      "w-4 h-4 shrink-0",
                      isActive
                        ? "text-primary-700 dark:text-primary-500"
                        : "text-text-tertiary-light dark:text-text-tertiary-dark",
                    )}
                  />
                  {!sidebarCollapsed && (
                    <>
                      <span
                        className={clsx(
                          "opacity-40 font-normal",
                          isActive && "text-primary-700 dark:text-primary-400",
                        )}
                      >
                        /
                      </span>
                      Completed
                    </>
                  )}
                </button>
              );
            })()}

            {/* Shared Lists */}
            {sharedLists.length > 0 && (
              <>
                {!sidebarCollapsed && (
                  <div className="px-3 pt-3 pb-1">
                    <span className="text-[10px] font-mono font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary-dark opacity-60">
                      Shared
                    </span>
                  </div>
                )}
                {sharedLists.map((list) => {
                  const slug = `shared:${list.id}`;
                  const isActive = currentView === slug;
                  return (
                    <button
                      key={list.id}
                      onClick={() => {
                        handleViewChange(slug);
                        setShowSettings(false);
                        setShowInsights(false);
                      }}
                      title={sidebarCollapsed ? list.name : undefined}
                      className={clsx(
                        "w-full flex items-center transition-all duration-150 rounded",
                        sidebarCollapsed
                          ? "justify-center px-0 py-2"
                          : "gap-2.5 px-3 py-2",
                        "font-mono text-[13px] font-medium",
                        isActive
                          ? "bg-primary-900/20 dark:bg-primary-900/20 text-primary-800 dark:text-primary-400"
                          : "text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark",
                      )}
                    >
                      <Share2
                        className={clsx(
                          "w-4 h-4 shrink-0",
                          isActive
                            ? "text-primary-700 dark:text-primary-500"
                            : "text-text-tertiary-light dark:text-text-tertiary-dark",
                        )}
                      />
                      {!sidebarCollapsed && (
                        <>
                          <span
                            className={clsx(
                              "opacity-40 font-normal",
                              isActive &&
                                "text-primary-700 dark:text-primary-400",
                            )}
                          >
                            /
                          </span>
                          {list.name}
                        </>
                      )}
                    </button>
                  );
                })}
              </>
            )}
          </nav>

          {/* Calendar widget — only on desktop, only when Google is connected */}
          <CalendarWidget
            collapsed={sidebarCollapsed}
            refreshNonce={calendarRefreshNonce}
            onOpenSettings={(tab) => {
              setSearchParams({ tab });
              setShowSettings(true);
              setShowInsights(false);
              setChatPanelOpen(false);
            }}
          />

          {/* Bottom: settings shortcuts */}
          <div className="px-1.5 py-2 border-t border-border-subtle-light dark:border-border-subtle-dark space-y-0.5">
            <button
              onClick={() => {
                setShowSettings(true);
                setShowInsights(false);
              }}
              title={sidebarCollapsed ? "Settings" : undefined}
              className={clsx(
                "w-full flex items-center font-mono text-[11px] text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors",
                sidebarCollapsed
                  ? "justify-center px-0 py-2"
                  : "gap-2.5 px-3 py-1.5",
              )}
            >
              <SettingsIcon className="w-3.5 h-3.5 shrink-0" />
              {!sidebarCollapsed && (
                <>
                  <span className="opacity-40">/</span> Settings
                </>
              )}
            </button>
            <button
              onClick={() => {
                setShowInsights(true);
                setShowSettings(false);
              }}
              title={sidebarCollapsed ? "Insights" : undefined}
              className={clsx(
                "w-full flex items-center font-mono text-[11px] text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors",
                sidebarCollapsed
                  ? "justify-center px-0 py-2"
                  : "gap-2.5 px-3 py-1.5",
              )}
            >
              <BarChart3 className="w-3.5 h-3.5 shrink-0" />
              {!sidebarCollapsed && (
                <>
                  <span className="opacity-40">/</span> Insights
                </>
              )}
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Mobile view selector removed -- the drawer and bottom bar handle navigation */}

          {showInsights ? (
            <ProductivityInsights onBack={() => setShowInsights(false)} />
          ) : showSettings ? (
            <SettingsLayout onClose={() => setShowSettings(false)} />
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Mobile: floating icons + full-screen content */}
              <div className="md:hidden flex flex-col h-full min-h-0 overflow-hidden">
                <MobileTopBar
                  currentViewName={
                    visibleViews.find((v) => v.slug === currentView)?.name ??
                    currentView
                  }
                  isChatActive={mobileTab === "chat"}
                  onOpenDrawer={() => setDrawerOpen(true)}
                  onToggleView={() => {
                    setMobileTab(mobileTab === "chat" ? "tasks" : "chat");
                  }}
                  onFiltersClick={() =>
                    setMobileFiltersOpen(!mobileFiltersOpen)
                  }
                />
                {mobileTab === "chat" ? (
                  <MobileChatView
                    onResponse={handleChatResponse}
                    context={chatContext}
                  />
                ) : (
                  // DO NOT add paddingTop here — the mobile top inset belongs
                  // on the DetailPanelLayout scroller so content scrolls
                  // under the frosted MobileTopBar. Adding it here kills scrolling.
                  <div className="flex flex-col h-full min-h-0 overflow-hidden bg-surface-base-light dark:bg-surface-base-dark">
                    <QuickFilters
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      showCompleted={showCompleted}
                      searchTerm={searchTerm}
                      onSortChange={handleSortChange}
                      onShowCompletedChange={handleShowCompletedChange}
                      onSearchChange={handleSearchChange}
                      mobileFiltersOpen={mobileFiltersOpen}
                      onMobileFiltersClose={() => setMobileFiltersOpen(false)}
                      isSemanticActive={isSemanticActive}
                      searchMode={searchMode}
                    />
                    <DetailPanelLayout
                      selectedTask={selectedTask}
                      onTaskClose={handleTaskClose}
                      onTaskComplete={handleTaskCompleteFromDetail}
                      onTaskReopen={handleTaskReopen}
                      onTaskUpdate={handleTaskUpdate}
                      onTaskSelectById={handleTaskSelectById}
                      isPendingCompletion={
                        selectedTask
                          ? pendingCompletions.has(selectedTask.id)
                          : false
                      }
                    >
                      <TaskList
                        tasks={displayTasks}
                        isLoading={isLoading}
                        onTaskSelect={handleTaskSelect}
                        selectedTaskId={selectedTask?.id}
                        onTaskComplete={handleTaskComplete}
                        onTaskUndo={handleTaskUndo}
                        onTaskReopen={handleTaskReopen}
                        pendingCompletions={pendingCompletionIds}
                        projectsMap={projectsMap}
                        onTaskSuggestDecompose={handleTaskSuggestDecompose}
                      />
                    </DetailPanelLayout>
                  </div>
                )}
              </div>

              {/* Desktop: existing layout with task list + drawer chat */}
              <div className="hidden md:flex flex-col h-full overflow-hidden">
                <QuickFilters
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  showCompleted={showCompleted}
                  searchTerm={searchTerm}
                  onSortChange={handleSortChange}
                  onShowCompletedChange={handleShowCompletedChange}
                  onSearchChange={handleSearchChange}
                  isSemanticActive={isSemanticActive}
                  searchMode={searchMode}
                  rightSlot={
                    <div className="flex items-center gap-1">
                      {!showInsights &&
                        !showSettings &&
                        visibleViews.find((v) => v.slug === currentView) && (
                          <button
                            onClick={() => setShareModalOpen(true)}
                            className="p-2 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark rounded transition-colors"
                            title="Share this view"
                          >
                            <Share2 size={16} />
                          </button>
                        )}
                      <ChatToggleButton
                        isOpen={isChatPanelOpen}
                        onToggle={toggleChatPanel}
                        pulse={false}
                      />
                    </div>
                  }
                />
                <DetailPanelLayout
                  selectedTask={selectedTask}
                  onTaskClose={handleTaskClose}
                  onTaskComplete={handleTaskCompleteFromDetail}
                  onTaskReopen={handleTaskReopen}
                  onTaskUpdate={handleTaskUpdate}
                  onTaskSelectById={handleTaskSelectById}
                  isPendingCompletion={
                    selectedTask
                      ? pendingCompletions.has(selectedTask.id)
                      : false
                  }
                  overridePanel={
                    isCreatingTask ? (
                      <TaskCreateForm
                        onCreated={(task) => {
                          setIsCreatingTask(false);
                          setSelectedTask(task);
                          void refetch();
                        }}
                        onCancel={() => setIsCreatingTask(false)}
                        onBrainDump={() => {
                          setIsCreatingTask(false);
                          setIsBrainDumpOpen(true);
                        }}
                      />
                    ) : undefined
                  }
                >
                  <>
                    <TaskList
                      tasks={filteredTasks}
                      isLoading={isLoading}
                      onTaskSelect={handleTaskSelect}
                      selectedTaskId={selectedTask?.id}
                      onTaskComplete={handleTaskComplete}
                      onTaskUndo={handleTaskUndo}
                      onTaskReopen={handleTaskReopen}
                      pendingCompletions={pendingCompletionIds}
                      projectsMap={projectsMap}
                    />
                  </>
                </DetailPanelLayout>
              </div>
            </div>
          )}
        </div>
        {/* Right-side chat panel (desktop only) */}
        <ChatPanel
          isOpen={isChatPanelOpen}
          onClose={() => setChatPanelOpen(false)}
          context={chatContext}
          onResponse={handleChatResponse}
        />
        <GettingStartedChecklist isChatActive={mobileTab === "chat"} />
      </div>

      {/* Brain Dump overlay — desktop + mobile */}
      {isBrainDumpOpen && (
        <BrainDumpOverlay
          onClose={() => setIsBrainDumpOpen(false)}
          onTasksCreated={refetch}
        />
      )}

      {/* PWA install prompt (mobile non-standalone only) */}
      <PwaInstallBanner />

      {/* Mobile bottom fade — mirrors MobileTopBar so the list dissolves into
          the bottom chrome band instead of ending on a hard line. Tasks tab
          only; the chat tab has its own fixed composer. Sits below the FAB
          (z-40) and Finish-setup pill (z-30). */}
      {mobileTab === "tasks" && <MobileBottomFade />}

      {/* Mobile quick-add FAB — only visible on tasks tab */}
      {mobileTab === "tasks" && <QuickAddFab onTaskCreated={refetch} />}

      {/* Mobile navigation drawer */}
      <MobileDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        views={visibleViews}
        currentView={currentView}
        onViewChange={(slug) => {
          handleViewChange(slug);
          setShowSettings(false);
          setShowInsights(false);
          setMobileTab("tasks");
        }}
        onSettingsClick={() => {
          setShowInsights(false);
          setShowSettings(true);
          setMobileTab("tasks");
        }}
        onInsightsClick={() => {
          setShowSettings(false);
          setShowInsights(true);
          setMobileTab("tasks");
        }}
      />

      {/* AI limit upgrade modal — shown when a 402 response is received */}
      {showUpgradeModal && (
        <UpgradeModal
          onClose={() => setShowUpgradeModal(false)}
          stripeConfigured={upgradeModalStripeConfigured}
        />
      )}

      {/* Share list modal */}
      {shareModalOpen &&
        (() => {
          const view = visibleViews.find((v) => v.slug === currentView);
          if (!view) return null;
          return (
            <ShareListModal
              targetType="view"
              targetId={currentView}
              defaultName={view.name}
              isOpen={shareModalOpen}
              onClose={() => setShareModalOpen(false)}
            />
          );
        })()}
    </Layout>
  );
}
